import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'
import { ConnectionCancellationPolicies, formatDate } from '@luxuryescapes/lib-refunds'

import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import useToggle from 'hooks/useToggle'
import BodyText from 'components/Luxkit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'components/Luxkit/Modal/Modal'
import LineRefundIcon from 'components/Luxkit/Icons/line/LineRefundIcon'
import TextLink from 'components/Luxkit/TextLink'

const CancellationWrapper = styled(Clickable)`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  font-size: ${props => props.theme.fontSize.sm};
  gap: ${rem(4)};
`

interface Props {
  cancellationPolicies: ConnectionCancellationPolicies;
  cancellationPolicy?: App.OfferCancellationPolicy;
  checkIn?: string;
  className?: string;
  hideNonRefundableMessage?: boolean;
  textLinkFormat?: boolean
  hideExcludesFlights?: boolean;
}

enum Refundable {
  Fully = 'Free cancellation',
  Partial = 'Partially refundable',
  Non = 'Non refundable'
}

function ConnectionCancellationPoliciesComponent(props: Props) {
  const { cancellationPolicies, cancellationPolicy, className, checkIn, hideNonRefundableMessage, textLinkFormat, hideExcludesFlights } = props
  const [isPolicyOpen, togglePolicy] = useToggle()

  const deadline = cancellationPolicies.policies[0]?.deadline ?? null
  const timezone = cancellationPolicies.policies[0]?.timezone ?? null
  let refundable: Refundable = Refundable.Non

  if (checkIn && deadline) {
    refundable = moment.utc().isBefore(checkIn) ? Refundable.Partial : Refundable.Non
  }

  if (deadline && moment.utc().isBefore(deadline)) {
    refundable = Refundable.Fully
  }

  const onClickIcon = (e) => {
    e.preventDefault()
    togglePolicy()
  }

  return <>
    <CancellationWrapper onClick={onClickIcon} className={className}>
      {!hideNonRefundableMessage && <>
        {refundable === Refundable.Fully && <>
          {textLinkFormat &&
            <TextLink
              size="medium"
              variant="highlight"
              underline={false}
              startIcon={<LineRefundIcon />}
            >
              {refundable}
            </TextLink>
          }
          {!textLinkFormat && <>
            <BodyText variant="medium" weight="semi-bold" colour="highlight-secondary" as="span">{refundable}</BodyText>
            {deadline &&
              <BodyText variant="medium" as="span">until {formatDate.formattedShortDateByTimezone(deadline, timezone)}</BodyText>
          }
            <SolidInfoCircleIcon colour="success" size="XS" />
          </>}
        </>}
        {refundable === Refundable.Partial && <>
          {textLinkFormat &&
            <TextLink
              size="medium"
              variant="highlight"
              underline={false}
              startIcon={<LineRefundIcon />}
            >
              {refundable}
            </TextLink>
          }
          {!textLinkFormat && <>
            <BodyText variant="medium" weight="semi-bold" colour="highlight-secondary" as="span">Partially refundable</BodyText>
            {checkIn &&
              <BodyText variant="medium" as="span">until {formatDate.formattedShortDateByTimezone(checkIn, timezone)}</BodyText>
            }
            <SolidInfoCircleIcon colour="success" size="XS" />
          </>}
        </>}
        {refundable === Refundable.Non && <>
          <BodyText variant="medium" as="span">{refundable}</BodyText>
          <SolidInfoCircleIcon size="XS" />
        </>}
      </>}
      {hideNonRefundableMessage && refundable === Refundable.Fully && <>
        {textLinkFormat &&
          <TextLink
          size="medium"
          variant="highlight"
          underline={false}
          startIcon={<LineRefundIcon />}
        >
            Free cancellation available
          </TextLink>
        }
        {!textLinkFormat && <>
          <BodyText variant="medium" weight="semi-bold" colour="highlight-secondary" as="span">Free cancellation available</BodyText>
          {deadline &&
            <BodyText variant="medium" as="span">until {formatDate.formattedShortDateByTimezone(deadline, timezone)}</BodyText>
        }
          <SolidInfoCircleIcon colour="success" size="XS" />
        </>}
      </>}
    </CancellationWrapper>
    <Modal
      title={refundable}
      isOpen={isPolicyOpen}
      onClose={onClickIcon}
    >
      {cancellationPolicies.description?.map(line => <BodyText variant="large" as="p" key={line}>{ line }</BodyText>)}
      {cancellationPolicy?.type == 'dynamic' &&
        <BodyText variant="large" as="p">Please note: cancellation policies apply to accommodation bookings only{hideExcludesFlights ? '' : ', excludes flights'}.</BodyText>
        }
      <HelpPane/>
    </Modal>
  </>
}

export default ConnectionCancellationPoliciesComponent
